import React from "react";
import ProductCard from "../productCard/ProductCard";
import { PrimaryPrice } from "../priceComponents";
import { COMPLEMENTARY_PRIMARY_PRICE } from "../priceComponents/priceTypes";
import { ProductContextProvider } from "../../context/product/ProductContext";
import {
  VariantContextProvider,
  useVariant,
} from "../../context/variant/VariantContext";
import { getPrimaryImage, getPrimaryVariant } from "../../utils/product";
import { StylingLookProductWithStockPrice } from "../../types/productTypes/stylingLookProduct";
import { Variant } from "../../types/productTypes/product";
import {
  fireWaysToStyleAddToBagAnalytics,
  fireWaysToStyleAddToBagMarketing,
} from "../../analytics/addToBag";
import saveForLater from "../../utils/saveForLater";

interface WaysToStyleProductCardProps {
  product: StylingLookProductWithStockPrice;
  lookProducts: StylingLookProductWithStockPrice[];
}

const WaysToStyleProductCardContent: React.FC<WaysToStyleProductCardProps> = ({
  product,
  lookProducts,
}) => {
  const { productPrice } = product;
  const { selectedVariant } = useVariant() as {
    selectedVariant?: { variantId: number };
  };
  const defaultColourWayId = getPrimaryVariant(product.variants)?.colourWayId;
  const variantIdentifier = selectedVariant
    ? `variantId:${selectedVariant.variantId}`
    : `colourWayId:${defaultColourWayId}`;

  const handleSaveClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    event.preventDefault();

    const identifier = selectedVariant
      ? { variantId: selectedVariant.variantId }
      : { colourWayId: defaultColourWayId };

    saveForLater({
      product,
      items: [{ productId: product.id, ...identifier }],
    });
  };

  const handleAddToBag = async ({
    selectedVariant,
  }: {
    selectedVariant: Variant;
  }) => {
    await fireWaysToStyleAddToBagAnalytics(lookProducts)({ selectedVariant });
    await fireWaysToStyleAddToBagMarketing()({ selectedVariant, product });
  };

  return (
    <ProductCard
      product={product}
      productLink={product.pdpUrl ?? null}
      handleSaveClick={handleSaveClick}
      variantIdentifier={variantIdentifier}
      variantSelectorId={`ways-to-style-variant-selector-${product.id}`}
      imageWidth={163}
      primaryImageUrl={getPrimaryImage(product.images)?.url ?? ""}
      fireAddToBagAnalytics={handleAddToBag}
      priceRenderProp={() =>
        productPrice ? (
          <PrimaryPrice
            priceData={productPrice}
            testId="product-price"
            priceType={COMPLEMENTARY_PRIMARY_PRICE}
          />
        ) : null
      }
    />
  );
};

const WaysToStyleProductCard: React.FC<WaysToStyleProductCardProps> = ({
  product,
  lookProducts,
}) => {
  return (
    <ProductContextProvider products={[product]} productId={product.id}>
      <VariantContextProvider context="pdp_wts">
        <WaysToStyleProductCardContent
          product={product}
          lookProducts={lookProducts}
        />
      </VariantContextProvider>
    </ProductContextProvider>
  );
};

export default WaysToStyleProductCard;
